import { ReactNode, useEffect } from "react";
import { PostHogProvider, usePostHog } from "@posthog/react";
import posthogJs, { type PostHog, type PostHogConfig } from "posthog-js";

import { toPhone } from "common/utils/formatting";
import { Optional } from "common/utils/types";

export type PostHogContext = Record<string, string | number | boolean | null>;

/**
 * The Vetcove product a set of events originates from. Mirrors the backend
 * PostHogProduct enum.
 *
 * Only PHD is emitted from this codebase; Vetcove Core is a separate app that
 * stamps CORE into the same shared PostHog project, so it is intentionally not
 * listed here.
 */
export enum PostHogProduct {
  PHD = "PHD",
}

/** The app a set of events originates from. Mirrors the backend PostHogApp enum. */
export enum PostHogApp {
  ClientFe = "CLIENT_FE",
  ClinicFe = "CLINIC_FE",
  Backend = "BACKEND",
}

/**
 * posthog-js init options sourced from the backend-controlled POSTHOG_OPTIONS
 * setting, which is the source of truth (defaults merged with any override).
 * The setting is trusted to be configured correctly; a malformed JSON string
 * falls back to no options so it can never break app load.
 * Available options: https://posthog.com/docs/libraries/js/config
 */
const postHogOptions: Partial<PostHogConfig> = (() => {
  try {
    return JSON.parse(window.POSTHOG_OPTIONS || "{}");
  } catch {
    return {};
  }
})();

/** Base properties supplied by ClientBundle._get_initial_user_data */
export type PostHogAnalyticsUser = {
  id: number;
  vetcove_profile_id: Optional<number>;
  email: Optional<string>;
  phone_number: Optional<number>;
  first_name: Optional<string>;
  last_name: Optional<string>;
};

export type PostHogAnalyticsIdentity = {
  uniqueId: string;
  // The shared Vetcove Core profile id, used to reconcile a person across PHD and Core.
  vetcoveProfileId: Optional<number>;
  email: Optional<string>;
  phoneNumber: Optional<number>;
  firstName: Optional<string>;
  lastName: Optional<string>;
};

/** A PostHog group to associate all captured events with. */
export type PostHogAnalyticsGroup = {
  type: string;
  key: string;
  properties?: Record<string, unknown>;
};

type PostHogAnalyticsProviderProps = {
  children: ReactNode;
  isEnabled: boolean;
  identity: PostHogAnalyticsIdentity | null;
  groups: PostHogAnalyticsGroup[];
  context: PostHogContext;
};

type PostHogSessionProps = {
  identity: PostHogAnalyticsIdentity | null;
  groups: PostHogAnalyticsGroup[];
  context: PostHogContext;
};

const PostHogSession = ({ identity, groups, context }: PostHogSessionProps) => {
  const posthog = usePostHog();

  useEffect(() => {
    if (identity) {
      posthog.identify(identity.uniqueId, {
        vetcove_profile_id: identity.vetcoveProfileId,
        email: identity.email,
        phone_number: toPhone(identity.phoneNumber),
        first_name: identity.firstName,
        last_name: identity.lastName,
      });
      // Anonymous in-app but PostHog still holds an identified user: a real
      // logout, so reset. A fresh anonymous visitor keeps a stable distinct id
      // across page loads, which avoids fragmenting anonymous sessions.
      // eslint-disable-next-line no-underscore-dangle -- _isIdentified is the SDK's identified-state check
    } else if (posthog._isIdentified()) {
      posthog.reset();
    }
    // resetGroups() before re-grouping so a removed group type stops attributing events to the previous one.
    posthog.resetGroups();
    groups.forEach(({ type, key, properties }) => {
      posthog.group(type, key, properties);
    });
    // re-register here so context survives reset() (which clears super properties).
    posthog.register(context);
  }, [identity, groups, context, posthog]);

  return null;
};

let postHogClient: PostHog | null = null;

/**
 * Returns the singleton PostHog client, initializing it on first use.
 *
 * We own initialization (rather than letting PostHogProvider do it from an
 * effect) so the client is ready before PostHogSession's effects run identify,
 * register, and group.
 */
const getPostHogClient = (): PostHog => {
  if (!postHogClient) {
    posthogJs.init(window.POSTHOG_PUBLIC_API_KEY, postHogOptions);
    postHogClient = posthogJs;
  }
  return postHogClient;
};

const PostHogAnalyticsProvider = ({
  children,
  identity,
  groups,
  isEnabled,
  context,
}: PostHogAnalyticsProviderProps) => {
  if (!isEnabled || !window.POSTHOG_PUBLIC_API_KEY) {
    return <>{children}</>;
  }

  return (
    <PostHogProvider client={getPostHogClient()}>
      <PostHogSession identity={identity} groups={groups} context={context} />
      {children}
    </PostHogProvider>
  );
};

export default PostHogAnalyticsProvider;
